<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <button class="close-button" @click="closeModal">✕</button>

      <div class="header">
        <img src="@assets/images/location-dot.svg" alt="Location Help Header" />
        <h2>{{ $t("components.locationhelppicker.ayuda_activar_ubicacion") }}</h2>
      </div>

      <div class="help-container">
        <div class="help-section" v-for="(instruction, index) in instructions" :key="index">
          <h3 class="help-title">{{ instruction.title }}</h3>
          <ol class="help-steps" v-if="instruction.steps">
            <li v-for="(step, stepIndex) in instruction.steps" :key="stepIndex">{{ step }}</li>
          </ol>
          <p v-if="instruction.note" class="help-note">{{ instruction.note }}</p>
        </div>
        
        <!-- Sección adicional sobre permisos de orientación -->
        <div class="help-section orientation-section">
          <h3 class="help-title">{{ $t("components.locationhelppicker.permisos_orientacion_titulo") }}</h3>
          <p class="help-note">{{ $t("components.locationhelppicker.permisos_orientacion_nota") }}</p>
          <ol class="help-steps">
            <li v-for="(step, stepIndex) in orientationSteps" :key="stepIndex">{{ step }}</li>
          </ol>
        </div>
      </div>

      <div class="actions">
        <button class="btn btn-primary" @click="closeModal">
          {{ $t("components.locationhelppicker.entendido") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Props y emits
const emit = defineEmits<{
  close: [];
}>();

// Detectar sistema operativo
function detectOS(): string {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }
  if (/Mac|Macintosh/.test(userAgent)) {
    return 'macos';
  }
  if (/Win/.test(userAgent)) {
    return 'windows';
  }
  return 'unknown';
}

// Detectar navegador
function detectBrowser(): string {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  if ((userAgent.indexOf("Opera") || userAgent.indexOf("OPR")) > -1) {
    return 'opera';
  }
  if (userAgent.indexOf("Edg") > -1) {
    return 'edge';
  }
  if (userAgent.indexOf("Chrome") > -1) {
    return 'chrome';
  }
  if (userAgent.indexOf("Safari") > -1) {
    return 'safari';
  }
  if (userAgent.indexOf("Firefox") > -1) {
    return 'firefox';
  }
  return 'unknown';
}

// Pasos para permisos de orientación
const orientationSteps = computed(() => {
  const os = detectOS();
  const steps: string[] = [];
  
  if (os === 'ios') {
    steps.push(t("components.locationhelppicker.orientacion_ios_paso1"));
    steps.push(t("components.locationhelppicker.orientacion_ios_paso2"));
    steps.push(t("components.locationhelppicker.orientacion_ios_paso3"));
  } else if (os === 'android') {
    steps.push(t("components.locationhelppicker.orientacion_android_paso1"));
    steps.push(t("components.locationhelppicker.orientacion_android_paso2"));
    steps.push(t("components.locationhelppicker.orientacion_android_paso3"));
  } else {
    steps.push(t("components.locationhelppicker.orientacion_generico_paso1"));
    steps.push(t("components.locationhelppicker.orientacion_generico_paso2"));
  }
  
  return steps;
});

// Generar instrucciones según OS y navegador
const instructions = computed(() => {
  const os = detectOS();
  const browser = detectBrowser();
  const instructionsList: Array<{ title: string; steps?: string[]; note?: string }> = [];

  // Instrucciones para Chrome
  if (browser === 'chrome') {
    if (os === 'windows' || os === 'macos') {
      instructionsList.push({
        title: t("components.locationhelppicker.chrome_pc_mac_titulo"),
        steps: [
          t("components.locationhelppicker.chrome_pc_mac_paso1"),
          t("components.locationhelppicker.chrome_pc_mac_paso2"),
          t("components.locationhelppicker.chrome_pc_mac_paso3"),
          t("components.locationhelppicker.chrome_pc_mac_paso4"),
          t("components.locationhelppicker.chrome_pc_mac_paso5")
        ]
      });
    } else if (os === 'android') {
      instructionsList.push({
        title: t("components.locationhelppicker.chrome_android_titulo"),
        steps: [
          t("components.locationhelppicker.chrome_android_paso1"),
          t("components.locationhelppicker.chrome_android_paso2"),
          t("components.locationhelppicker.chrome_android_paso3"),
          t("components.locationhelppicker.chrome_android_paso4")
        ]
      });
    } else if (os === 'ios') {
      instructionsList.push({
        title: t("components.locationhelppicker.chrome_ios_titulo"),
        steps: [
          t("components.locationhelppicker.chrome_ios_paso1"),
          t("components.locationhelppicker.chrome_ios_paso2"),
          t("components.locationhelppicker.chrome_ios_paso3")
        ]
      });
    }
  }

  // Instrucciones para Edge
  if (browser === 'edge') {
    if (os === 'windows' || os === 'macos') {
      instructionsList.push({
        title: t("components.locationhelppicker.edge_pc_mac_titulo"),
        steps: [
          t("components.locationhelppicker.edge_pc_mac_paso1"),
          t("components.locationhelppicker.edge_pc_mac_paso2"),
          t("components.locationhelppicker.edge_pc_mac_paso3"),
          t("components.locationhelppicker.edge_pc_mac_paso4"),
          t("components.locationhelppicker.edge_pc_mac_paso5")
        ]
      });
    } else if (os === 'android') {
      instructionsList.push({
        title: t("components.locationhelppicker.edge_android_titulo"),
        steps: [
          t("components.locationhelppicker.edge_android_paso1"),
          t("components.locationhelppicker.edge_android_paso2"),
          t("components.locationhelppicker.edge_android_paso3"),
          t("components.locationhelppicker.edge_android_paso4")
        ]
      });
    } else if (os === 'ios') {
      instructionsList.push({
        title: t("components.locationhelppicker.edge_ios_titulo"),
        steps: [
          t("components.locationhelppicker.edge_ios_paso1"),
          t("components.locationhelppicker.edge_ios_paso2"),
          t("components.locationhelppicker.edge_ios_paso3")
        ]
      });
    }
  }

  // Instrucciones para Opera
  if (browser === 'opera') {
    if (os === 'windows' || os === 'macos') {
      instructionsList.push({
        title: t("components.locationhelppicker.opera_pc_mac_titulo"),
        steps: [
          t("components.locationhelppicker.opera_pc_mac_paso1"),
          t("components.locationhelppicker.opera_pc_mac_paso2"),
          t("components.locationhelppicker.opera_pc_mac_paso3"),
          t("components.locationhelppicker.opera_pc_mac_paso4"),
          t("components.locationhelppicker.opera_pc_mac_paso5")
        ]
      });
    } else if (os === 'android') {
      instructionsList.push({
        title: t("components.locationhelppicker.opera_android_titulo"),
        steps: [
          t("components.locationhelppicker.opera_android_paso1"),
          t("components.locationhelppicker.opera_android_paso2"),
          t("components.locationhelppicker.opera_android_paso3"),
          t("components.locationhelppicker.opera_android_paso4")
        ]
      });
    } else if (os === 'ios') {
      instructionsList.push({
        title: t("components.locationhelppicker.opera_ios_titulo"),
        steps: [
          t("components.locationhelppicker.opera_ios_paso1"),
          t("components.locationhelppicker.opera_ios_paso2"),
          t("components.locationhelppicker.opera_ios_paso3")
        ]
      });
    }
  }

  // Si no hay instrucciones específicas, mostrar instrucciones genéricas
  if (instructionsList.length === 0) {
    instructionsList.push({
      title: t("components.locationhelppicker.instrucciones_genericas_titulo"),
      steps: [
        t("components.locationhelppicker.instrucciones_genericas_paso1"),
        t("components.locationhelppicker.instrucciones_genericas_paso2"),
        t("components.locationhelppicker.instrucciones_genericas_paso3")
      ],
      note: t("components.locationhelppicker.instrucciones_genericas_nota")
    });
  }

  return instructionsList;
});

function closeModal() {
  emit("close");
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
}

.modal-content {
  background: var(--background-default-default, #FFF);
  border-radius: var(--modal-radius, 0.5rem);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--background-normal-hover, #f0f0f0);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  color: var(--text-default-caption, #667790);
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    background: var(--background-normal-alt-hover, #d6d6d6);
    color: var(--text-default-default, #000);
  }
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-lg);
  text-align: left;

  img {
    width: 36px;
    height: 36px;
  }

  h2 {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-heading-h3, 1.25rem);
    font-weight: 700;
    margin: 0;
  }
}

.help-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: 0 var(--spacing-md);
}

.help-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.help-title {
  color: var(--text-default-default, #000);
  font-size: var(--font-size-heading-h4, 1.125rem);
  font-weight: 700;
  margin: 0;
}

.help-steps {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 0.875rem);
  line-height: var(--line-height-body-sm, 1.125rem);
  margin: 0;
  padding-left: var(--spacing-md);

  li {
    margin-bottom: var(--spacing-xs);
  }
}

.help-note {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 0.875rem);
  line-height: var(--line-height-body-sm, 1.125rem);
  font-style: italic;
  margin: var(--spacing-xs) 0 0 0;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-default-default, #C2CDDD);
}
</style>
